import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Trans } from '@lingui/macro';
import { t } from '@lingui/macro';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { validators } from '@ft-frontend/core';
import { onlineService } from '@ft-frontend/core';
import { Image } from '@ft-frontend/core';

import useStartingInfo from 'contexts/starting_info/useStartingInfo';

import useOnMount from 'hooks/useOnMount';
import { useOnlineAPI } from 'hooks/useOnlineAPI';

import { resetFlow } from 'reducers/flow/flowSlice';
import { resetRecycle } from 'reducers/recycle/recycleSlice';
import { setPageName } from 'reducers/ui/uiSlice';

import { urlSearchFormatter } from 'utils/formatters';

export default function FindDevice() {
  const { id: clientID } = useStartingInfo();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [manufacturersList, setManufacturersList] = useState(undefined);
  const [categoriesList, setCategoriesList] = useState(undefined);
  const [familiesList, setFamiliesList] = useState(undefined);
  const [devicesList, setDevicesList] = useState(undefined);

  const [selectedManufacturer, setManufacturer] = useState(undefined);
  const [selectedCategory, setCategory] = useState(undefined);
  const [selectedFamily, setFamily] = useState(undefined);
  const [selectedDevice, setDevice] = useState(undefined);

  const { exceptionHandler, apiMessageHandler } = useOnlineAPI(
    onlineService.endpoints.getTradeInDeviceList,
    { initialRequest: false },
  );

  const getInitialOption = ({ list = [], callback = () => {}, params }) => {
    list.length === 1 && callback({ target: { value: list[0]?.code } }, params);
    return list.length > 1 ? undefined : list[0]?.code;
  };

  const getTradeInOptionsList = useCallback(
    async (data = {}) => {
      const response = await onlineService.getTradeInDeviceList(
        {
          clientID,
          ...data,
        },
        {
          exceptionHandler,
          errorHandler: apiMessageHandler('error'),
          warningHandler: apiMessageHandler('warning'),
        },
      );

      return response.data;
    },
    [apiMessageHandler, clientID, exceptionHandler],
  );

  useOnMount(() => {
    dispatch(resetRecycle());
    dispatch(resetFlow());
    dispatch(setPageName('UPG-OnlineTradeInSearch'));

    let fetchData = async () => {
      let { manufacturers } = await getTradeInOptionsList();
      setManufacturersList(manufacturers);
    };
    fetchData();
  });

  const handleChangeManufacturer = async (evt) => {
    setManufacturer(evt.target.value);

    let { categories } = await getTradeInOptionsList({
      manufacturer: evt.target.value,
    });
    setCategoriesList(categories);
    setCategory(
      getInitialOption({
        list: categories,
        params: { manufacturer: evt.target.value },
        callback: handleChangeCategory,
      }),
    );
    setFamily(undefined);
    setDevice(undefined);
  };

  const handleChangeCategory = async (evt, params = {}) => {
    setCategory(evt.target.value);

    let { families } = await getTradeInOptionsList({
      manufacturer: selectedManufacturer,
      category: evt.target.value,
      ...params,
    });
    setFamiliesList(families);

    setFamily(
      getInitialOption({
        list: families,
        params: {
          category: evt.target.value,
          ...params,
        },
        callback: handleChangeFamily,
      }),
    );
    setDevice(undefined);
  };

  const handleChangeFamily = async (evt, params = {}) => {
    setFamily(evt.target.value);

    let { devices } = await getTradeInOptionsList({
      manufacturer: selectedManufacturer,
      category: selectedCategory,
      family: evt.target.value,
      ...params,
    });
    setDevicesList(devices);

    setDevice(devices.length === 1 && devices[0]);
  };

  const handleChangeDevice = async (evt) => {
    let selectedOption = devicesList.find(
      (option) => option.partNumber === evt.target.value,
    );

    setDevice(selectedOption);
  };

  const handleNavigation = () => {
    navigate(
      urlSearchFormatter(`/online/tradein/estimation`, {
        deviceCode: selectedDevice.partNumber,
        deviceName: selectedDevice.description,
      }),
    );
  };

  return (
    <Grid item xs={12} md={6}>
      <Paper
        variant='outlined'
        sx={{
          backgroundColor: 'common.white',
          padding: (theme) => theme.spacing(3),
        }}
        elevation={0}
      >
        <Stack spacing={3}>
          {validators.isIframe() && (
            <Image
              key={`td-Renew-logo`}
              aspectRatio='337/64'
              filePath={`Techdata/TD-SYNNEX_Renew.png`}
              alt={'TD Synnex logo'}
              style={{ maxHeight: '64px' }}
              height={64}
              data-testid='tradein-tdsynnex-logo'
            />
          )}

          <Typography variant='h3' textAlign='center' component='h1'>
            <Trans id='tradein.standalone.form.page.title'>
              TD Renew provides you with a provisional valuation of your current
              device.
            </Trans>
          </Typography>
          <FormControl fullWidth>
            <InputLabel id='manufacturers-label'>
              <Trans id='tradein.standalone.form.input.placeholder.manufacturer'>
                Select your device manufacturer
              </Trans>
            </InputLabel>
            <Select
              labelId='manufacturers-label'
              id='manufacturers-select'
              value={selectedManufacturer || ''}
              label={t({
                id: 'tradein.standalone.form.input.placeholder.manufacturer',
                message: 'Select your device manufacturer',
              })}
              onChange={handleChangeManufacturer}
              data-testid='finddevice-manufacturer-select'
            >
              {manufacturersList?.map((manufacturer) => (
                <MenuItem
                  value={manufacturer.code}
                  key={`${manufacturer.code}-${manufacturer.name}`}
                  data-testid={`finddevice-manufacturer-option-${manufacturer.code}`}
                >
                  {manufacturer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id='categories-label'>
              <Trans id='tradein.standalone.form.input.placeholder.category'>
                Select its category
              </Trans>
            </InputLabel>
            <Select
              labelId='categories-label'
              id='categories-select'
              disabled={!selectedManufacturer}
              value={selectedCategory || ''}
              label={t({
                id: 'tradein.standalone.form.input.placeholder.category',
                message: 'Select its category',
              })}
              onChange={handleChangeCategory}
              data-testid='finddevice-categories-select'
            >
              {categoriesList?.map((category) => (
                <MenuItem
                  value={category.code}
                  key={`${category.code}-${category.name}`}
                  data-testid={`finddevice-categories-option-${category.code}`}
                >
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id='family-label'>
              <Trans id='tradein.standalone.form.input.placeholder.family'>
                Select its family
              </Trans>
            </InputLabel>
            <Select
              labelId='family-label'
              id='family-select'
              disabled={!selectedCategory}
              value={selectedFamily || ''}
              label={t({
                id: 'tradein.standalone.form.input.placeholder.family',
                message: 'Select its family',
              })}
              onChange={handleChangeFamily}
              data-testid='finddevice-families-select'
            >
              {familiesList?.map((family) => (
                <MenuItem
                  value={family.code}
                  key={`${family.code}-${family.name}`}
                  data-testid={`finddevice-families-option-${family.code}`}
                >
                  {family.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id='device-label'>
              <Trans id='tradein.standalone.form.input.placeholder.device'>
                Select its model
              </Trans>
            </InputLabel>
            <Select
              labelId='device-label'
              id='device-select'
              disabled={!selectedFamily}
              value={selectedDevice?.partNumber || ''}
              label={t({
                id: 'tradein.standalone.form.input.placeholder.device',
                message: 'Select its model',
              })}
              onChange={handleChangeDevice}
              data-testid='finddevice-devices-select'
            >
              {devicesList?.map((device) => (
                <MenuItem
                  value={device.partNumber}
                  key={`${device.partNumber}-${device.description}`}
                  data-testid={`finddevice-devices-option-${device.partNumber}`}
                >
                  {device.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            data-testid='submit-btn'
            disabled={!selectedDevice}
            onClick={handleNavigation}
            color='secondary'
            variant='contained'
            size='large'
            sx={{ width: 'fit-content' }}
          >
            <Trans id='tradein.standalone.form.submit'>Calculate value</Trans>
          </Button>
        </Stack>
      </Paper>
    </Grid>
  );
}
